import _ from 'lodash';

import Search from '../records/Search';
import AppDispatcher from '../dispatcher/AppDispatcher';
import JourneyPlanService from '../services/JourneyPlanService';
import FareOnlyService from '../services/FareOnlyService';
import { ActionTypes } from '../constants/AppConstants';
import { InvalidSearchError } from '../errors/Search';
import ConfigService from '../services/ConfigService';
import PromoCode from '../records/PromoCode';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import PromoCodeActions from './PromoCodeActions';

const SearchActions = {

    async loadFromParams(params, query) {
        let search = await Search.deserialize(params, query);
        search = search.set('submitted', true);

        this.checkPromoFromQuery(search);

        return this.load(search);
    },

    load (search) {
        AppDispatcher.dispatch({
            search,
            actionType: ActionTypes.SEARCH_LOADED
        });
        return search;
    },

    update (searchRecord) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SEARCH_UPDATED,
            search: searchRecord
        });
    },

    async checkPromoFromQuery(search) {
        const promoCode = search.getPromoCode();
        const enablePromoDiscount = FeatureFlagStore.get('enablePromoDiscount');
        const enablePromoDiscountValidation = FeatureFlagStore.get('enablePromoDiscountValidation');

        if (!enablePromoDiscount || !promoCode) {
            return;
        }

        if (enablePromoDiscountValidation) {
            const validatedPromoCode = await PromoCodeActions.validatePromoCode(promoCode);
            const newSearch = search.setPromoCode(validatedPromoCode);

            this.update(newSearch);
        } else {
            const promo = new PromoCode({ promoCode });
            await PromoCodeActions.apply(promo);
            const newSearch = search.setPromoCode(promo);
            this.update(newSearch);
        }
    },

    async preFillPromoFromQuery(query) {
        const enablePromoDiscount = FeatureFlagStore.get('enablePromoDiscount');

        if (!enablePromoDiscount) {
            return;
        }

        const newSearch = new Search({ promoCode: new PromoCode(query) });
        this.update(newSearch);
    },

    async checkTocOnlyRoute (search) {
        const result = await ConfigService.checkTocOnlyRoute(search);
        AppDispatcher.dispatch({
            actionType: ActionTypes.CHECK_TOC_ONLY_ADD,
            search,
            result
        });
        return result;
    },

    async search ({ search, user }) {
        if (search.departNow) {
            search = search.setOutwardTimeNow();
        }

        if (!search.isValid()) {
            const errors = search.getValidationErrors().toJS().join(', ');

            AppDispatcher.dispatch({
                actionType: ActionTypes.SEARCH_FAILED,
                search
            });

            return Promise.reject(new InvalidSearchError(`Failed check: ${errors}`, search));
        }

        AppDispatcher.dispatch({
            actionType: ActionTypes.SEARCH_STARTED,
            search
        });

        const result = await JourneyPlanService.search({ search, user });

        AppDispatcher.dispatch({
            actionType: ActionTypes.SEARCH_FINISHED,
            search,
            outward: result.outward,
            inward: result.inward,
            hasServiceProvider: result.hasServiceProvider
        });

        return {
            search,
            result
        };
    },

    async searchFares ({ search, user }) {
        if (!search.isValid()) {
            const errors = search.getValidationErrors().toJS().join(', ');

            AppDispatcher.dispatch({
                actionType: ActionTypes.FARE_ONLY_SEARCH_FAILED,
                search
            });

            return Promise.reject(new InvalidSearchError(`Failed check: ${errors}`, search));
        }

        AppDispatcher.dispatch({
            actionType: ActionTypes.FARE_ONLY_SEARCH_STARTED,
            search
        });

        const fares = await FareOnlyService.search(search, user);

        AppDispatcher.dispatch({
            actionType: ActionTypes.FARE_ONLY_SEARCH_FINISHED,
            search,
            fares
        });

        return {
            search,
            fares
        };
    },

    async showEarlier (user, search, tripDirection, services) {
        const updatedSearch = search.earlier(tripDirection, services);

        AppDispatcher.dispatch({
            search: updatedSearch,
            tripDirection,
            actionType: ActionTypes.SHOW_EARLIER_TRAINS_STARTED
        });

        const result = await JourneyPlanService.search({ search: updatedSearch, user });

        AppDispatcher.dispatch({
            user,
            result,
            tripDirection,
            search: updatedSearch,
            actionType: ActionTypes.SHOW_EARLIER_TRAINS_FINISHED
        });

        return {
            updatedSearch,
            result
        };
    },

    async showLater (user, search, tripDirection, services) {
        const updatedSearch = search.later(tripDirection, services);

        AppDispatcher.dispatch({
            search: updatedSearch,
            tripDirection,
            actionType: ActionTypes.SHOW_LATER_TRAINS_STARTED
        });

        const result = await JourneyPlanService.search({ search: updatedSearch, user });

        AppDispatcher.dispatch({
            user,
            result,
            tripDirection,
            search: updatedSearch,
            actionType: ActionTypes.SHOW_LATER_TRAINS_FINISHED
        });

        return {
            updatedSearch,
            result
        };
    },

    resetJourneyStore () {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.RESET_JOURNEY_STORE
        }));
    },
};

export default SearchActions;



// WEBPACK FOOTER //
// ./src/actions/SearchActions.js